import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  brutalfilesMe,
  brutalfilesListUsers,
  brutalfilesGetTransferHistory,
  brutalfilesListPublicShares,
  brutalfilesCreatePublicShare,
  isAuthenticated,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppMainHeader } from "@/components/AppMainHeader";
import { UserAvatar } from "@/components/UserAvatar";
import {
  Search,
  Loader2,
  Users,
  FileText,
  Wifi,
  WifiOff,
  Link2,
  Copy,
  Paperclip,
  Circle,
} from "lucide-react";
import { useBrutalFilesP2P } from "@/hooks/useBrutalFilesP2P";

const MAX_PUBLIC_SHARE_TOTAL_BYTES = 512 * 1024 * 1024; // 512 MB

export default function BrutalFilesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [publicShares, setPublicShares] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [sendingFileTo, setSendingFileTo] = useState(null);
  const [sendProgress, setSendProgress] = useState(null);
  const [dragOverUser, setDragOverUser] = useState(null);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [shareTitle, setShareTitle] = useState("");
  const [shareDescription, setShareDescription] = useState("");
  const [shareFiles, setShareFiles] = useState([]);
  const [creatingShare, setCreatingShare] = useState(false);
  const [publicShareUrl, setPublicShareUrl] = useState("");
  const [publicShareStatus, setPublicShareStatus] = useState("");
  const [publicShareOnline, setPublicShareOnline] = useState(false);
  const [dragOverShareBox, setDragOverShareBox] = useState(false);
  const fileInputRef = useRef(null);

  const myEmail = (me?.email || user?.email || "").toLowerCase();
  const { peers, wsConnected, sendFileTo, receivingFile, requestNotificationPermission, hostPublicShare } = useBrutalFilesP2P(myEmail);

  useEffect(() => {
    if (myEmail) requestNotificationPermission();
  }, [myEmail, requestNotificationPermission]);

  const loadAll = useCallback(async () => {
    if (!isAuthenticated()) return;
    setIsLoading(true);
    try {
      const [meRes, usersRes, historyRes, publicSharesRes] = await Promise.all([
        brutalfilesMe().catch(() => null),
        brutalfilesListUsers().catch(() => ({ users: [] })),
        brutalfilesGetTransferHistory().catch(() => ({ history: [] })),
        brutalfilesListPublicShares().catch(() => ({ shares: [] })),
      ]);
      setMe(meRes || null);
      setUsers(usersRes.users || []);
      setTransferHistory(historyRes.history || []);
      setPublicShares(publicSharesRes.shares || []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar datos");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { replace: true });
      return;
    }
    if (user?.permissions?.archivos === false) {
      if (user?.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else if (user?.permissions?.formularios !== false) navigate("/formularios", { replace: true });
      else if (user?.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
      else if (user?.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user?.permissions?.proveedores !== false) navigate("/proveedores", { replace: true });
      else if (user?.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user?.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
      return;
    }
    loadAll();
  }, [loadAll, navigate, user?.permissions?.archivos, user?.permissions?.candidatos, user?.permissions?.formularios, user?.permissions?.proveedores]);

  const myEmailLower = (me?.email || user?.email || "").toLowerCase();
  const otherUsers = users.filter((u) => (u.email || "").toLowerCase() !== myEmailLower);
  const peerSet = new Set((peers || []).map((e) => (e || "").toLowerCase()));

  // Orden: quien se conecta pasa primero; al desconectarse mantiene posición hasta que otro se conecte
  const orderedDisplayRef = useRef([]);
  const order = orderedDisplayRef.current;
  const peerList = Array.from(peerSet);
  peerList.forEach((email) => {
    const idx = order.indexOf(email);
    if (idx > 0) order.splice(idx, 1);
    if (idx !== 0) order.unshift(email);
  });
  otherUsers.forEach((u) => {
    const e = (u.email || "").toLowerCase();
    if (e && !order.includes(e)) order.push(e);
  });

  const filteredUsers = otherUsers.filter((u) => {
    if (!userSearch.trim()) return true;
    const q = userSearch.trim().toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.nombre || "").toLowerCase().includes(q);
  });

  const sortedFilteredUsers = [...filteredUsers].sort((a, b) => {
    const ia = order.indexOf((a.email || "").toLowerCase());
    const ib = order.indexOf((b.email || "").toLowerCase());
    const iA = ia === -1 ? 1e9 : ia;
    const iB = ib === -1 ? 1e9 : ib;
    return iA - iB;
  });

  const handleSendFileToUser = useCallback(
    async (targetUser, file) => {
      const email = (targetUser.email || "").toLowerCase();
      if (!file) return;
      if (!wsConnected) {
        toast.error("Sin conexión al servidor. Espera un momento.");
        return;
      }
      if (!peerSet.has(email)) {
        toast.error(`${targetUser.nombre || targetUser.email} no está conectado. Debe tener la pestaña Archivos abierta.`);
        return;
      }
      setSendProgress({ sent: 0, total: file.size });
      try {
        await sendFileTo(email, file, (sent, total) => setSendProgress({ sent, total }));
        toast.success(`Archivo "${file.name}" enviado`);
        loadAll();
      } catch (err) {
        toast.error(err?.message || "Error al enviar archivo");
      } finally {
        setSendProgress(null);
      }
    },
    [wsConnected, peerSet, sendFileTo, loadAll]
  );

  const handleDragOver = useCallback((e, user) => {
    e.preventDefault();
    e.stopPropagation();
    if (!peerSet.has((user?.email || "").toLowerCase())) return;
    e.dataTransfer.dropEffect = "copy";
    setDragOverUser(user?.email ?? null);
  }, [peerSet]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverUser(null);
  }, []);

  const handleDrop = useCallback(
    (e, targetUser) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverUser(null);
      const file = e.dataTransfer?.files?.[0];
      if (file && targetUser) handleSendFileToUser(targetUser, file);
    },
    [handleSendFileToUser]
  );

  const formatBytes = (n) => {
    if (n == null || n === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(n) / Math.log(k));
    return `${parseFloat((n / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  const totalShareSize = shareFiles.reduce((acc, file) => acc + (file?.size || 0), 0);

  const handlePickShareFiles = useCallback((e) => {
    const picked = Array.from(e.target?.files || []);
    setShareFiles(picked);
  }, []);

  const handleShareBoxClick = useCallback(() => {
    setOpenShareModal(true);
  }, []);

  const handleShareBoxDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setDragOverShareBox(true);
  }, []);

  const handleShareBoxDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverShareBox(false);
  }, []);

  const handleShareBoxDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverShareBox(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) setShareFiles(files);
    setOpenShareModal(true);
  }, []);

  const handleCreatePublicShare = useCallback(async () => {
    if (!wsConnected) {
      toast.error("Debes estar conectado para hospedar el enlace.");
      return;
    }
    if (!shareTitle.trim()) {
      toast.error("Añade un título.");
      return;
    }
    if (shareFiles.length === 0) {
      toast.error("Añade al menos un archivo.");
      return;
    }
    if (totalShareSize > MAX_PUBLIC_SHARE_TOTAL_BYTES) {
      toast.error("El peso total supera 512 MB para enlace público.");
      return;
    }
    setCreatingShare(true);
    setPublicShareStatus("Creando enlace...");
    setPublicShareOnline(false);
    try {
      const filesMeta = shareFiles.map((f) => ({ name: f.name, size: f.size, type: f.type || "" }));
      const data = await brutalfilesCreatePublicShare({
        title: shareTitle.trim(),
        description: shareDescription.trim(),
        filesMeta,
        expiresInHours: 24,
      });
      const fullUrl = `${window.location.origin}${data.share_url}`;
      setPublicShareUrl(fullUrl);
      setPublicShareStatus("Enlace activo. Mantén esta pestaña abierta para compartir.");
      hostPublicShare(data.share_id, shareFiles, (evt) => {
        if (evt.type === "host_ready") {
          setPublicShareOnline(true);
          setPublicShareStatus("Enlace activo. Mantén esta pestaña abierta para compartir.");
        }
        if (evt.type === "host_error") {
          setPublicShareOnline(false);
          setPublicShareStatus(evt.detail || "No se pudo activar el enlace.");
        }
        if (evt.type === "receiver_joined") setPublicShareStatus("Receptor conectado. Enviando archivos...");
        if (evt.type === "sending_complete") setPublicShareStatus("Transferencia completada.");
        if (evt.type === "receiver_done") setPublicShareStatus("Descarga finalizada por el receptor.");
        if (evt.type === "sending_error" || evt.type === "peer_error" || evt.type === "host_error") {
          setPublicShareStatus(evt.detail || "Hubo un error durante la transferencia.");
        }
      });
      toast.success("Enlace generado");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "No se pudo crear el enlace");
      setPublicShareStatus("");
      setPublicShareOnline(false);
    } finally {
      setCreatingShare(false);
    }
  }, [wsConnected, shareTitle, shareFiles, shareDescription, hostPublicShare, totalShareSize]);

  const copyPublicShareUrl = useCallback(async () => {
    if (!publicShareUrl) return;
    try {
      await navigator.clipboard.writeText(publicShareUrl);
      toast.success("Enlace copiado");
    } catch (_) {
      toast.error("No se pudo copiar el enlace");
    }
  }, [publicShareUrl]);

  useEffect(() => {
    if (!wsConnected) setPublicShareOnline(false);
  }, [wsConnected]);

  const copyUrl = useCallback(async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado");
    } catch (_) {
      toast.error("No se pudo copiar el enlace");
    }
  }, []);

  return (
    <div className="h-dvh min-w-0 flex flex-col overflow-hidden">
      <AppMainHeader title="Archivos" />

      <div className="max-w-7xl mx-auto px-6 py-6 flex-1 min-h-0 overflow-y-auto w-full mobile-nav-scroll">
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="secondary" className="text-xs font-normal min-w-9 tabular-nums justify-center">
            {otherUsers.length}
          </Badge>
          {wsConnected ? (
            <Badge variant="outline" className="text-xs font-normal text-green-600 border-green-300 gap-1">
              <Wifi className="w-3 h-3" strokeWidth={1.5} />
              Conectado
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs font-normal text-amber-600 border-amber-300 gap-1">
              <WifiOff className="w-3 h-3" strokeWidth={1.5} />
              Reconectando…
            </Badge>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
          </div>
        ) : (
          <Tabs defaultValue="usuarios" className="space-y-4">
            <TabsList className="h-9">
              <TabsTrigger value="usuarios">Usuarios</TabsTrigger>
              <TabsTrigger value="historial">Historial</TabsTrigger>
            </TabsList>

            <TabsContent value="usuarios" className="mt-4 space-y-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 bg-background"
                />
              </div>
              {otherUsers.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <Users className="w-12 h-12 text-muted-foreground/50 mb-4" strokeWidth={1.5} />
                    <p className="text-muted-foreground">Solo tú en la app por ahora</p>
                    <p className="text-sm text-muted-foreground mt-1">Cuando haya más usuarios, aparecerán aquí para enviar archivos P2P.</p>
                  </CardContent>
                </Card>
              ) : sortedFilteredUsers.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">Ningún usuario coincide con la búsqueda</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <Card
                    className={`overflow-hidden flex flex-col items-center text-center transition-all bg-card border-border cursor-pointer ${
                      dragOverShareBox ? "ring-2 ring-primary ring-offset-2" : ""
                    }`}
                    onClick={handleShareBoxClick}
                    onDragOver={handleShareBoxDragOver}
                    onDragLeave={handleShareBoxDragLeave}
                    onDrop={handleShareBoxDrop}
                    style={{ minHeight: 220 }}
                  >
                    <div className="relative w-full pt-4 pb-2 px-4 flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                        <Link2 className="w-7 h-7" strokeWidth={1.5} />
                      </div>
                      <p className="font-semibold text-foreground truncate w-full px-1">
                        Obtener un enlace
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Abre modal o arrastra archivos
                      </p>
                    </div>
                    <CardContent className="pt-0 pb-4 px-4 text-center w-full">
                      <p className="text-xs text-muted-foreground">
                        Genera y comparte una URL pública
                      </p>
                    </CardContent>
                  </Card>
                  {sortedFilteredUsers.map((u) => {
                    const isOnline = peerSet.has((u.email || "").toLowerCase());
                    const isDragOver = dragOverUser === u.email;
                    return (
                      <Card
                        key={u.email}
                        className={`overflow-hidden flex flex-col items-center text-center transition-all bg-card border-border ${
                          isOnline ? "" : "opacity-60"
                        } ${isDragOver ? "ring-2 ring-primary ring-offset-2" : ""}`}
                        onDragOver={(e) => handleDragOver(e, u)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, u)}
                        style={{ minHeight: 220 }}
                      >
                        <div className="relative w-full pt-4 pb-2 px-4 flex flex-col items-center">
                          <span
                            className={`absolute top-3 right-3 w-3 h-3 rounded-full shrink-0 ${
                              isOnline ? "bg-green-500" : "bg-red-500"
                            }`}
                            aria-label={isOnline ? "En línea" : "Desconectado"}
                          />
                          <UserAvatar user={u} size={64} className="mb-3" />
                          <p className="font-semibold text-foreground truncate w-full px-1">
                            {u.nombre || u.email}
                          </p>
                          <span className="text-xs text-muted-foreground bg-muted rounded-full px-2.5 py-0.5 mt-1 truncate max-w-full">
                            @{u.email?.split("@")[0] || u.email}
                          </span>
                          <p className="text-xs text-muted-foreground mt-2">
                            {isOnline ? "Conectado" : "Desconectado"}
                          </p>
                        </div>
                        <CardContent className="pt-0 pb-4 px-4 text-center w-full">
                          {isOnline ? (
                            <p className="text-xs text-muted-foreground">
                              Arrastra un archivo aquí para enviar
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground/80">
                              No disponible para recibir
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="historial" className="mt-4 space-y-3">
              <p className="text-xs text-muted-foreground max-w-xl">
                Los archivos que te envían se descargan en la carpeta de descargas de tu navegador. Para elegir otra carpeta, configúralo en tu navegador (ej. Chrome: Configuración → Descargas).
              </p>
              {transferHistory.length === 0 && publicShares.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="w-12 h-12 text-muted-foreground/50 mb-4" strokeWidth={1.5} />
                    <p className="text-muted-foreground">Aún no hay historial</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {publicShares.length > 0 ? (
                    <Card>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {publicShares.map((entry) => {
                            const fullUrl = `${window.location.origin}${entry.share_url}`;
                            return (
                              <div key={entry.share_id} className="flex items-center gap-4 px-4 py-3">
                                <Link2 className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{entry.title || "Enlace público"}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {entry.files_count} archivo(s) · {formatBytes(entry.total_size)} · {entry.status === "expired" ? "Caducado" : "Activo"}
                                  </p>
                                </div>
                                <Button type="button" size="sm" variant="outline" onClick={() => copyUrl(fullUrl)} className="gap-1.5">
                                  <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  Compartir
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                  {transferHistory.length > 0 ? (
                    <Card>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {transferHistory.map((entry, i) => (
                            <div key={i} className="flex items-center gap-4 px-4 py-3">
                              <FileText className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{entry.file_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {entry.from_email} → {entry.to_email} · {formatBytes(entry.file_size)}
                                </p>
                              </div>
                              <span className="text-xs text-muted-foreground shrink-0">{formatDate(entry.completed_at)}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {receivingFile && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border bg-card px-4 py-3 shadow-lg flex items-center gap-3 max-w-sm">
          <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">Recibiendo: {receivingFile.fileName}</p>
            <p className="text-xs text-muted-foreground">
              {receivingFile.size ? `${Math.round((receivingFile.received / receivingFile.size) * 100)}%` : "…"}
            </p>
          </div>
        </div>
      )}

      {sendProgress && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border bg-card px-4 py-3 shadow-lg flex items-center gap-3 max-w-sm">
          <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" strokeWidth={1.5} />
          <p className="text-sm font-medium">Enviando… {sendProgress.total ? `${Math.round((sendProgress.sent / sendProgress.total) * 100)}%` : ""}</p>
        </div>
      )}

      <Dialog open={openShareModal} onOpenChange={setOpenShareModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Compartir por enlace público</DialogTitle>
            <DialogDescription>
              Añade título, texto y archivos. Se generará un enlace público y la descarga será P2P.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Título" value={shareTitle} onChange={(e) => setShareTitle(e.target.value)} />
            <textarea
              value={shareDescription}
              onChange={(e) => setShareDescription(e.target.value)}
              placeholder="Párrafo descriptivo"
              className="w-full min-h-24 rounded-md border bg-background px-3 py-2 text-sm"
            />
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handlePickShareFiles} />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-1.5">
                <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                Añadir archivos
              </Button>
              <span className="text-xs text-muted-foreground">{shareFiles.length} archivo(s) · {formatBytes(totalShareSize)}</span>
            </div>
            {shareFiles.length > 0 ? (
              <div className="max-h-44 overflow-auto rounded-md border p-2 space-y-1">
                {shareFiles.map((file) => (
                  <div key={`${file.name}-${file.size}`} className="text-sm flex items-center justify-between gap-3">
                    <span className="truncate">{file.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatBytes(file.size)}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {publicShareUrl ? (
              <div className="rounded-md border p-2 bg-muted/40 space-y-2">
                <p className="text-xs text-muted-foreground">Enlace público</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-xs ${publicShareOnline ? "text-green-600" : "text-amber-600"}`}>
                    <Circle className="w-2.5 h-2.5 fill-current" />
                    {publicShareOnline ? "Online" : "Offline"}
                  </span>
                  <Input readOnly value={publicShareUrl} />
                  <Button type="button" variant="outline" size="icon" onClick={copyPublicShareUrl}>
                    <Copy className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
                {publicShareStatus ? <p className="text-xs text-muted-foreground">{publicShareStatus}</p> : null}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={handleCreatePublicShare}
              disabled={creatingShare || !wsConnected || shareFiles.length === 0 || !shareTitle.trim()}
            >
              {creatingShare ? "Generando..." : "Generar enlace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
